import { enumEntrySummaryState, enumEntrySummarySubmissionType } from '@data-layer/enums/entry-summaries';
import { enumPaymentState } from '@data-layer/enums/payments';
import { CustomStateModel } from '@data-layer/models/common';
import { CustomState } from '@data-layer/enums/common';
import { UtilsHelper } from '@helpers/utils-helper';

export class EntrySummaryStatusHelper {
  /**
   * Importer (USER mode) masking: the broker checkpoint is invisible to the importer, so state 50
   * (BROKER_REVIEW_PENDING) is displayed as 120 (BROKER_PROCESSING_PENDING). Callers in SUPPORT mode
   * (admin/support and the customs-broker area) pass isUserMode=false → the real state. #13102.
   * Pre-condition: state-50 entries are always BROKER_ASSISTED (the only path into 50); for any other
   * submissionType getFirstText(120) would return an empty label — acceptable as that case can't occur.
   */
  public static resolveDisplayState(state: enumEntrySummaryState, isUserMode: boolean): enumEntrySummaryState {
    return state === enumEntrySummaryState.BROKER_REVIEW_PENDING && isUserMode ? enumEntrySummaryState.BROKER_PROCESSING_PENDING : state;
  }

  /**
   * Sent = any state past NOT_SENT that is not DELETED. An undefined state counts as not sent.
   */
  public static isSent(state: enumEntrySummaryState): boolean {
    return state > enumEntrySummaryState.NOT_SENT && state !== enumEntrySummaryState.DELETED;
  }

  private static deletedStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.CBP_DELETED,
    enumEntrySummaryState.CBP_DELETE_PENDING,
    enumEntrySummaryState.DELETED,
  ];

  public static getPointColor(state: enumEntrySummaryState, paymentState: enumPaymentState): string {
    let pointColor: string;

    switch (state) {
      case enumEntrySummaryState.NOT_SENT:
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
        pointColor = 'green';
        if (paymentState === enumPaymentState.PENDING) {
          pointColor = 'orange';
        }
        break;
      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        pointColor = 'orange';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
        pointColor = 'blue';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        pointColor = 'error';
        break;
      default:
        pointColor = null;
        break;
    }

    return pointColor;
  }

  public static getHintText(state: enumEntrySummaryState, paymentState: enumPaymentState, showSecondTextForDeleted: boolean): string {
    let hintText: string;
    if (this.deletedStates.includes(state) && showSecondTextForDeleted) {
      hintText = paymentState === enumPaymentState.SUCCESS ? 'PAID' : 'DRAFT';
    }

    if (hintText) {
      hintText = `STATUS.HINT_TEXT.${hintText}`;
      return hintText;
    }

    if (state === enumEntrySummaryState.NOT_SENT && paymentState === enumPaymentState.PENDING) {
      hintText = 'STATUS.FIRST_TEXT.PENDING_PAYMENT';
      return hintText;
    }

    switch (state) {
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
        hintText = 'READY_FOR_SEND';
        break;
      case enumEntrySummaryState.BOND_PENDING:
        hintText = 'BOND_PURCHASE_IN_PROGRESS';
        break;
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
        hintText = 'AWAITING_RESPONSE';
        break;
      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
        hintText = 'DUTY_CALCULATION';
        break;
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        hintText = 'NO_BILL_ON_FILE';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
        hintText = 'AWAITING_RESPONSE';
        break;
      case enumEntrySummaryState.AWAITING_ARRIVAL:
        hintText = 'AWAITING_ARRIVAL';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        hintText = 'ACTIONS_NEEDED';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
        hintText = 'DUTIES_CALCULATED';
        break;
      default:
        hintText = null;
        break;
    }

    if (!!hintText) {
      hintText = `STATUS.HINT_TEXT.${hintText}`;
    }

    return hintText;
  }

  public static getHintTextColor(state: enumEntrySummaryState): string {
    let textColor: string;

    switch (state) {
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        textColor = 'error';
        break;
      default:
        textColor = 'dark-gray';
        break;
    }

    return textColor;
  }

  public static getSecondText(state: enumEntrySummaryState): string {
    let secondText: string;

    switch (state) {
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
        secondText = 'SENT';
        break;
      case enumEntrySummaryState.SENT_TO_CBP:
        secondText = 'RECEIVED_BY_CBP';
        break;
      case enumEntrySummaryState.ACCEPTED:
        secondText = 'ACCEPTED_BY_CBP';
        break;
      case enumEntrySummaryState.APPROVED:
        secondText = 'RELEASED_BY_CBP';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
        secondText = 'SENT_TO_CBP';
        break;
      default:
        secondText = null;
        break;
    }

    if (!!secondText) {
      secondText = `STATUS.SECOND_TEXT.${secondText}`;
    }

    return secondText;
  }

  public static getSecondTextColor(state: enumEntrySummaryState): string {
    let secondTextColor: string;

    switch (state) {
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.APPROVED:
        secondTextColor = 'green';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.ACCEPTED:
        secondTextColor = 'blue';
        break;
      default:
        secondTextColor = 'grey2';
        break;
    }

    return secondTextColor;
  }

  public static getFirstText(
    state: enumEntrySummaryState,
    paymentState: enumPaymentState,
    submissionType?: enumEntrySummarySubmissionType,
    customStateHeader?: string,
    customState?: CustomStateModel<CustomState>,
  ): string {
    let firstText: string;

    // #9070
    if (paymentState === enumPaymentState.PENDING) {
      return 'STATUS.FIRST_TEXT.DRAFT';
    }

    switch (state) {
      case enumEntrySummaryState.NOT_SENT:
      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
        firstText = 'DRAFT';
        break;
      case enumEntrySummaryState.BROKER_REVIEW_PENDING:
        firstText = 'BROKER_PENDING';
        break;
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
      case enumEntrySummaryState.BOND_PENDING:
        firstText = 'SENT';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.APPROVED:
        firstText = submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ? 'BROKER' : '';
        break;
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        firstText = 'CORRECTION_NEEDED';
        break;
      case enumEntrySummaryState.UNRECOVERABLE:
        firstText = 'SUPPORT_NEEDED';
        break;
      case enumEntrySummaryState.REJECTED:
        firstText = 'REJECTED';
        break;
      case enumEntrySummaryState.DELETED:
      case enumEntrySummaryState.CBP_DELETED:
        firstText = 'DELETED';
        break;
      case enumEntrySummaryState.CBP_DELETE_PENDING:
        firstText = 'REMOVAL_PENDING';
        break;
      case enumEntrySummaryState.CUSTOM:
        if (!UtilsHelper.isNullOrUndefined(customState)) {
          return this.getESCustomState(customState.header);
          break;
        } else if (!UtilsHelper.isNullOrUndefined(customStateHeader)) {
          return this.getESCustomState(Number(customStateHeader));
        }

        return 'STATUS_ES.CUSTOM';
        break;
      default:
        firstText = 'DRAFT';
        break;
    }

    if (!!firstText) {
      firstText = `STATUS.FIRST_TEXT.${firstText}`;
    }

    return firstText;
  }

  public static getFirstTextColor(state: enumEntrySummaryState, paymentState: enumPaymentState): string {
    let firstTextColor: string;

    // #10828
    if (paymentState === enumPaymentState.PENDING) {
      firstTextColor = 'grey2';
      return firstTextColor;
    }

    switch (state) {
      case enumEntrySummaryState.NOT_SENT:
        firstTextColor = 'grey2';
        break;
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
      case enumEntrySummaryState.BOND_PENDING:
        firstTextColor = 'blue';
        break;
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
      case enumEntrySummaryState.REJECTED:
      case enumEntrySummaryState.UNRECOVERABLE:
      case enumEntrySummaryState.CBP_DELETED:
      case enumEntrySummaryState.DELETED:
      case enumEntrySummaryState.CBP_DELETE_PENDING:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        firstTextColor = 'error';
        break;
      case enumEntrySummaryState.BROKER_REVIEW_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.APPROVED:
        firstTextColor = 'purple';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
        firstTextColor = 'primary-purple';
        break;
      default:
        firstTextColor = 'grey2';
        break;
    }

    return firstTextColor;
  }

  private static getESCustomState(customState: number): string {
    switch (customState) {
      case CustomState.CANCELING:
        return 'CANCELING';
      case CustomState.CANCELED:
        return 'CANCELED';
      case CustomState.REFUNDED:
        return 'REFUNDED';
    }
  }
}
